// utils/dateUtils.js

/**
 * Hebrew month names for localization
 */
const HEBREW_MONTHS = [
  'בינואר', 'בפברואר', 'במרץ', 'באפריל', 'במאי', 'ביוני',
  'ביולי', 'באוגוסט', 'בספטמבר', 'באוקטובר', 'בנובמבר', 'בדצמבר'
];

export const HEBREW_MONTH_NAMES = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
];

export const HEBREW_MONTH_NAMES_SHORT = [
  'ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ',
  'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצ'
];

const HEBREW_DAYS = [
  'יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי', 'יום שישי', 'שבת'
];

const HEBREW_DAYS_SHORT = [
  'א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'
];

/**
 * Parse YYYY-MM-DD string without creating Date object
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Object} - {year, month, day} where month is 1-based
 */
function parseDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    throw new Error('Invalid date string provided');
  }

  const parts = dateString.split('-');
  if (parts.length !== 3) {
    throw new Error('Date string must be in YYYY-MM-DD format');
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Basic validation
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error('Invalid date components');
  }

  if (month < 1 || month > 12) {
    throw new Error('Month must be between 1-12');
  }

  if (day < 1 || day > 31) {
    throw new Error('Day must be between 1-31');
  }

  return { year, month, day };
}

/**
 * Get day of week for a given date without timezone issues
 * Uses Zeller's congruence algorithm
 * @param {number} year 
 * @param {number} month - 1-based month
 * @param {number} day 
 * @returns {number} - Day of week (0=Sunday, 6=Saturday)
 */
function getDayOfWeek(year, month, day) {
  // Zeller's congruence - adjust month and year for January/February
  if (month < 3) {
    month += 12;
    year -= 1;
  }
  
  const k = year % 100;
  const j = Math.floor(year / 100);
  
  const h = (day + Math.floor((13 * (month + 1)) / 5) + k + Math.floor(k / 4) + Math.floor(j / 4) - 2 * j) % 7;
  
  // Convert to Sunday=0 format
  return (h + 5) % 7;
}

/**
 * Format date string to DD.MM.YYYY format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date DD.MM.YYYY
 */
export function formatDateDots(dateString) {
  try {
    const { year, month, day } = parseDateString(dateString);
    return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Fallback to original string
  }
}

/**
 * Format date string to Hebrew long format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Hebrew formatted date like "יום ראשון, 4 באוגוסט"
 */
export function formatDateHebrewLong(dateString) {
  try {
    const { year, month, day } = parseDateString(dateString);
    const dayOfWeek = getDayOfWeek(year, month, day);
    
    const hebrewDay = HEBREW_DAYS[dayOfWeek];
    const hebrewMonth = HEBREW_MONTHS[month - 1]; // Convert to 0-based for array access
    
    return `${hebrewDay}, ${day} ${hebrewMonth}`;
  } catch (error) {
    console.error('Error formatting Hebrew date:', error);
    return dateString;
  }
}

/**
 * Format date string to Hebrew short format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Hebrew formatted date like "ב׳, 4 אוג׳"
 */
export function formatDateHebrewShort(dateString) {
  try {
    const { year, month, day } = parseDateString(dateString);
    const dayOfWeek = getDayOfWeek(year, month, day);
    
    const hebrewDayShort = HEBREW_DAYS_SHORT[dayOfWeek];
    const hebrewMonthShort = HEBREW_MONTH_NAMES[month - 1].substring(0, 3) + '׳';
    
    return `${hebrewDayShort}, ${day} ${hebrewMonthShort}`;
  } catch (error) {
    console.error('Error formatting Hebrew short date:', error);
    return dateString;
  }
}


 export const formatHebrewMonth = (date) => {
    const m = date.getMonth();
    const y = date.getFullYear().toString().slice(2);
    return `${HEBREW_MONTH_NAMES[m]} ${y}`;
  };

/**
 * Format date string with year to Hebrew format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Hebrew formatted date like "4 באוגוסט 2025"
 */
export function formatDateHebrewWithYear(dateString) {
  try {
    const { year, month, day } = parseDateString(dateString);
    const hebrewMonth = HEBREW_MONTHS[month - 1];
    
    return `${day} ${hebrewMonth} ${year}`;
  } catch (error) {
    console.error('Error formatting Hebrew date with year:', error);
    return dateString;
  }
}

/**
 * Check if date string represents today
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export function isToday(dateString) {
  try {
    const today = new Date();
    const todayString = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
    return dateString === todayString;
  } catch (error) {
    return false;
  }
}

/**
 * Check if date string represents tomorrow
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export function isTomorrow(dateString) {
  try {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowString = `${tomorrow.getFullYear()}-${(tomorrow.getMonth() + 1).toString().padStart(2, '0')}-${tomorrow.getDate().toString().padStart(2, '0')}`;
    return dateString === tomorrowString;
  } catch (error) {
    return false;
  }
}

/**
 * Get relative date text in Hebrew (today/tomorrow/date)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - "היום", "מחר", or formatted date
 */
export function getRelativeDateHebrew(dateString) {
  if (isToday(dateString)) {
    return 'היום';
  }
  if (isTomorrow(dateString)) {
    return 'מחר';
  }
  return formatDateHebrewLong(dateString);
}

/**
 * Compare two date strings
 * @param {string} date1 - First date in YYYY-MM-DD format
 * @param {string} date2 - Second date in YYYY-MM-DD format
 * @returns {number} - -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDates(date1, date2) {
  try {
    // Simple string comparison works for YYYY-MM-DD format
    if (date1 < date2) return -1;
    if (date1 > date2) return 1;
    return 0;
  } catch (error) {
    console.error('Error comparing dates:', error);
    return 0;
  }
}

/**
 * Format date for display in appointment cards
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date for UI
 */
export function formatAppointmentDate(dateString) {
  if (isToday(dateString)) {
    return 'היום';
  }
  if (isTomorrow(dateString)) {
    return 'מחר';
  }
  return formatDateDots(dateString);
}

/**
 * Default export object with all utilities
 */
export default {
  formatDateDots,
  formatDateHebrewLong,
  formatDateHebrewShort,
  formatDateHebrewWithYear,
  formatAppointmentDate,
  formatHebrewMonth,
  getRelativeDateHebrew,
  isToday,
  isTomorrow,
  compareDates
};